/**
 * Copyright © Binary Anvil, Inc. All rights reserved.
 * See https://binaryanvil.com/software/license for license details.
 */

import {useState, useEffect} from "react";
import {extractScripts} from "@/app/utils/extractScripts";
import dynamic from 'next/dynamic'
const EmailSubscribeForm = dynamic(() => import("@/app/components/common/EmailSubscribeForm"), {ssr: false});
const LazyLoad = dynamic(() => import("@/app/components/LazyLoad"), {ssr: false});

const Html = (props: {
    children?:any,
    variation?: string,
}) => {
    const {variation} = props;
    const [htmlContent, setHtmlContent] = useState('');
    const [htmlScripts, setHtmlScripts] = useState<string[]>([]);
    const content = props.children.join('');
    const isWithScripts = variation && variation === 'with-scripts';

    // Step 1: extract and render HTML content (puts the container div in the DOM)
    useEffect(() => {
        if (!isWithScripts) return;
        const {scripts, cleanedHtml} = extractScripts(content);
        setHtmlContent(cleanedHtml);
        setHtmlScripts(scripts);
    }, [content, isWithScripts]);

    // Step 2: load scripts only after htmlContent has been rendered to DOM
    useEffect(() => {
        if (!htmlScripts.length) return;

        const addedScripts: HTMLScriptElement[] = [];
        const parser = new DOMParser();

        const externalScripts = htmlScripts.filter(s =>
            parser.parseFromString(s, 'text/html').querySelector('script')?.src
        );
        const inlineScripts = htmlScripts.filter(s => {
            const el = parser.parseFromString(s, 'text/html').querySelector('script');
            return el?.textContent && !el?.getAttribute('src');
        });

        const loadExternal = Promise.all(externalScripts.map(s => new Promise<void>((resolve, reject) => {
            const src = parser.parseFromString(s, 'text/html').querySelector('script')?.src;
            if (!src) return resolve();

            // Remove existing script with same src to force re-execution
            document.querySelector(`script[src="${src}"]`)?.remove();

            const script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.onload = () => resolve();
            script.onerror = reject;
            document.body.appendChild(script);
            addedScripts.push(script);
        })));

        loadExternal
            .then(() => {
                inlineScripts.forEach(s => {
                    const textContent = parser.parseFromString(s, 'text/html').querySelector('script')?.textContent;
                    if (!textContent) return;

                    const script = document.createElement('script');
                    script.textContent = textContent;
                    document.body.appendChild(script);
                    addedScripts.push(script);
                });
            })
            .catch(error => console.error('Script loading error:', error));

        return () => {
            addedScripts.forEach(s => s.remove());
        };
    }, [htmlScripts]);

    if(variation && variation === 'subscribe-form-link') {
        return <EmailSubscribeForm />
    }

    if(variation && variation === 'lazy') {
        return <LazyLoad shiftBeforeLoad={0}>
            <div className='html-block' dangerouslySetInnerHTML={{__html: isWithScripts ? htmlContent : content}} />
        </LazyLoad>
    }

    return (
        <>
            <div className='html-block' dangerouslySetInnerHTML={{__html: isWithScripts ? htmlContent : content}} />
        </>
    );
}

export default Html;